// @owners { team: patients }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  type CompleteVerificationParams,
  type StartVerificationParams,
  type VerificationCommsChannel,
} from '~shared/actions/verifications';
import { get, post } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { useMutation, useQuery } from '~shared/react-query';

const v3Options = { version: 'v3' } as const;

export const verifications = createQueryKeys('verifications', {
  identityVerificationNeeded: ({ token, commsChannel }: { token: string; commsChannel: string }) => ({
    queryKey: [token, commsChannel],
    queryFn: async (): Promise<boolean> => {
      const response = await get(
        '/communications/verifications/identity_verification_needed',
        { token, comms_channel: commsChannel },
        v3Options,
      );
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response.identity_verification_needed;
    },
  }),
});

/**
 * Whether the patient must confirm their identity (last name + DOB) before a comms
 * verification link can complete. Used by the unauthenticated verification pages.
 */
export const useIdentityVerificationNeeded = ({ token, commsChannel }: { token: string; commsChannel: string }) => {
  return useQuery({
    ...verifications.identityVerificationNeeded({ token, commsChannel }),
    // One-shot unauthenticated check from an emailed link: an invalid/expired token will
    // never succeed, so fail fast instead of the default 3 retries (~15s of spinner).
    retry: false,
  });
};

type VerifyCommsAndUserParams = {
  lastName: string;
  dob: string | null | undefined;
  token: string;
  commsChannel: string;
};

/**
 * Verify a comms channel along with the patient's identity (last name + DOB).
 * Resolves to whether verification succeeded; rejects only on API errors.
 */
export const useVerifyCommsAndUser = () => {
  return useMutation({
    mutationFn: async ({ lastName, dob, token, commsChannel }: VerifyCommsAndUserParams): Promise<boolean> => {
      const response = await post(
        '/communications/verifications/verify_comms_channel_and_user',
        { last_name: lastName, dob, token, comms_channel: commsChannel },
        v3Options,
      );
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response.verified;
    },
  });
};

/**
 * Verify an email or phone comms channel from a verification-link token.
 * Resolves to whether verification succeeded; rejects only on API errors.
 */
export const useVerifyComms = () => {
  return useMutation({
    mutationFn: async ({ commsChannel, token }: { commsChannel: 'email' | 'phone'; token: string }) => {
      const path =
        commsChannel === 'phone'
          ? '/communications/phone_verifications/verify'
          : '/communications/email_verifications/verify';
      const params = commsChannel === 'phone' ? { token } : { confirmation_token: token };
      const response = await post(path, params, v3Options);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return Boolean(response.verified);
    },
  });
};

/** Request a fresh verification link for a comms channel. */
export const useRequestVerification = () => {
  return useMutation({
    mutationFn: async ({ token, commsChannel }: { token: string; commsChannel: string }) => {
      const response = await post(
        '/communications/verifications/request_verification',
        { token, comms_channel: commsChannel },
        v3Options,
      );
      if (response.error || !response.success) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response;
    },
  });
};

/** Start a verification (e.g. duplicate-account check during onboarding). */
export const useStartVerification = () => {
  return useMutation({
    mutationFn: async (params: StartVerificationParams) => {
      const response = await post('/communications/verifications/start_verification', params, v3Options);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response;
    },
  });
};

export type CompleteVerificationResult = {
  phone_verification_id?: number;
  email_verification_id?: number;
};

/** Complete a verification with the code the patient received. */
export const useCompleteVerification = () => {
  return useMutation({
    mutationFn: async (params: CompleteVerificationParams): Promise<CompleteVerificationResult> => {
      const response = await post('/communications/verifications/complete_verification', params, v3Options);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      const { phone_verification_id, email_verification_id } = response;
      return { phone_verification_id, email_verification_id };
    },
  });
};

/** Mark a verification link as sent to the wrong person ("not me"). */
export const useMarkWrongUser = () => {
  return useMutation({
    mutationFn: async ({ token, commsChannel }: { token: string; commsChannel: string }) => {
      if (!token) {
        throw new Error('No token provided');
      }
      if (commsChannel !== 'email' && commsChannel !== 'phone') {
        throw new Error(`Invalid comms channel: ${commsChannel}`);
      }
      const response = await post(`/communications/${commsChannel}_verifications/not_me`, { token }, v3Options);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return true;
    },
  });
};

export type { CompleteVerificationParams, StartVerificationParams, VerificationCommsChannel };
